/**
 * CreatorLinkStudio — administrer offentlig link-side.
 *
 * Creators kan legge til, redigere og slette egne lenker uten admin-godkjenning.
 * QR-kode-opplasting per lenke.
 * Bio-advarsel mot Snap-blokkerende ord.
 */
import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Link as RouterLink, Navigate } from "react-router-dom";
import {
  ArrowLeft, ExternalLink, Copy, Check, ChevronUp, ChevronDown,
  Plus, Trash2, QrCode, Pencil, X, AlertTriangle, Upload,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import Layout from "../components/Layout";
import api from "../lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Ord som kan trigge Snap/andre link-preview-blokker
const SENSITIVE_WORDS = [
  "18+", "18 år", "18år", "adult", "erotisk", "erotikk", "erotic",
  "xxx", "voksen", "porno", "porn", "nsfw", "onlyfans", "of ", "fansly",
  "nakenbi", "naken",
];

function detectSensitive(text) {
  if (!text) return [];
  const low = text.toLowerCase();
  return SENSITIVE_WORDS.filter((w) => low.includes(w));
}

function SensitiveWarning({ text, className = "" }) {
  const hits = useMemo(() => detectSensitive(text), [text]);
  if (hits.length === 0) return null;
  return (
    <div className={`border-l-2 border-[var(--magenta)] pl-3 py-2 text-xs text-[var(--cream-soft)] flex items-start gap-2 ${className}`}>
      <AlertTriangle className="h-3.5 w-3.5 text-[var(--magenta)] shrink-0 mt-0.5" />
      <div>
        <div className="text-[var(--cream)] font-medium mb-1">Advarsel: ord som kan blokke lenken</div>
        Fjern eller endre: <span className="font-mono">{hits.join(", ")}</span>. Snap og andre plattformer
        blokker forhåndsvisninger som inneholder disse ordene.
      </div>
    </div>
  );
}

function LinkItemRow({ item, isFirst, isLast, onEdit, onDelete, onMove, onUploadQr, onDeleteQr, onToggle }) {
  const fileRef = useRef(null);
  const qrUrl = item.qr_image_path
    ? `${API}/media?path=${encodeURIComponent(item.qr_image_path)}`
    : null;

  return (
    <div className="flex items-start gap-3 border border-white/10 px-3 py-3" data-testid={`link-row-${item.id}`}>
      <div className="flex flex-col gap-0.5 shrink-0 pt-1">
        <button
          onClick={() => onMove(item, "up")}
          disabled={isFirst}
          className="text-[var(--cream-soft)] hover:text-[var(--cream)] disabled:opacity-30"
          data-testid={`link-up-${item.id}`}
        >
          <ChevronUp className="h-4 w-4" />
        </button>
        <button
          onClick={() => onMove(item, "down")}
          disabled={isLast}
          className="text-[var(--cream-soft)] hover:text-[var(--cream)] disabled:opacity-30"
          data-testid={`link-down-${item.id}`}
        >
          <ChevronDown className="h-4 w-4" />
        </button>
      </div>

      <div className="flex-1 min-w-0">
        <div className="font-manrope text-sm text-[var(--cream)] font-medium truncate">{item.label}</div>
        <a
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          className="font-mono text-xs text-[var(--cream-soft)] hover:text-[var(--magenta)] truncate block"
        >
          {item.url}
        </a>
        {item.clicks > 0 && (
          <div className="font-manrope text-xs text-[var(--cream-soft)] mt-1">
            {item.clicks} klikk
          </div>
        )}

        {qrUrl && (
          <div className="flex items-center gap-2 mt-2">
            <img src={qrUrl} alt="QR" className="w-16 h-16 border border-white/20" data-testid={`link-qr-${item.id}`} />
            <button
              onClick={() => onDeleteQr(item)}
              className="btn-ghost text-xs px-2 py-1 inline-flex items-center gap-1"
              data-testid={`remove-qr-${item.id}`}
            >
              <X className="h-3 w-3" /> Fjern QR
            </button>
          </div>
        )}
      </div>

      <div className="flex flex-col items-end gap-2 shrink-0">
        <label className="relative inline-flex items-center cursor-pointer" data-testid={`toggle-${item.id}`}>
          <input
            type="checkbox"
            checked={!!item.is_public}
            onChange={(e) => onToggle(item, e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-white/10 peer-checked:bg-[var(--magenta)] rounded-full peer transition-colors relative after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:after:translate-x-5" />
        </label>

        <div className="flex items-center gap-1">
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onUploadQr(item, f);
              e.target.value = "";
            }}
            data-testid={`upload-qr-input-${item.id}`}
          />
          <button
            onClick={() => fileRef.current?.click()}
            className="btn-ghost text-xs p-1.5"
            title="Last opp QR-kode-bilde"
            data-testid={`upload-qr-${item.id}`}
          >
            <QrCode className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => onEdit(item)}
            className="btn-ghost text-xs p-1.5"
            title="Rediger"
            data-testid={`edit-${item.id}`}
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => onDelete(item)}
            className="btn-ghost text-xs p-1.5 hover:text-[var(--magenta)]"
            title="Slett"
            data-testid={`delete-${item.id}`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

function EditModal({ item, onClose, onSave }) {
  const [label, setLabel] = useState(item.label || "");
  const [url, setUrl] = useState(item.url || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!label.trim() || !url.trim()) {
      toast.error("Fyll ut både tittel og URL");
      return;
    }
    setSaving(true);
    try {
      await onSave({ label: label.trim(), url: url.trim() });
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke lagre");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="edit-modal">
      <div
        className="bg-[var(--noir)] border border-white/20 max-w-md w-full p-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-fraunces italic text-2xl text-[var(--cream)]">Rediger lenke</h3>

        <div>
          <label className="block font-manrope text-sm text-[var(--cream)] mb-1">Tittel</label>
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value.slice(0, 60))}
            className="w-full bg-transparent border border-white/20 focus:border-[var(--magenta)] outline-none px-3 py-2 text-sm text-[var(--cream)]"
            data-testid="edit-label-input"
          />
        </div>

        <div>
          <label className="block font-manrope text-sm text-[var(--cream)] mb-1">URL</label>
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value.slice(0, 500))}
            className="w-full bg-transparent border border-white/20 focus:border-[var(--magenta)] outline-none px-3 py-2 text-sm text-[var(--cream)] font-mono"
            data-testid="edit-url-input"
          />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="btn-ghost text-sm px-4" data-testid="edit-cancel">Avbryt</button>
          <button onClick={save} disabled={saving} className="btn-magenta text-sm px-6" data-testid="edit-save">
            {saving ? "Lagrer…" : "Lagre"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CreatorLinkStudio() {
  const { user, loading } = useAuth();
  const [data, setData] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [bio, setBio] = useState("");
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newUrl, setNewUrl] = useState("");
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/me/link-page");
      setData(r.data);
      setEnabled(!!r.data.enabled);
      setBio(r.data.bio || "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke laste.");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return null;
  if (!user) return <Navigate to="/" replace />;

  const publicUrl = data ? `https://allelenker.no/link/${data.slug}` : "";

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.patch("/me/link-page/settings", { enabled, bio });
      toast.success("Lagret");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke lagre");
    } finally {
      setSaving(false);
    }
  };

  const addItem = async () => {
    if (!newLabel.trim() || !newUrl.trim()) {
      toast.error("Fyll ut både tittel og URL");
      return;
    }
    setAdding(true);
    try {
      await api.post("/me/link-page/items", {
        label: newLabel.trim(),
        url: newUrl.trim(),
        is_public: true,
      });
      setNewLabel("");
      setNewUrl("");
      toast.success("Lenke lagt til");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke legge til");
    } finally {
      setAdding(false);
    }
  };

  const toggleItem = async (item, next) => {
    try {
      await api.patch(`/me/link-page/items/${item.id}`, { is_public: next });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke oppdatere");
    }
  };

  const editItem = async (item, updates) => {
    await api.patch(`/me/link-page/items/${item.id}`, updates);
    toast.success("Oppdatert");
    load();
  };

  const deleteItem = async (item) => {
    if (!window.confirm(`Slett «${item.label}»?`)) return;
    try {
      await api.delete(`/me/link-page/items/${item.id}`);
      toast.success("Slettet");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke slette");
    }
  };

  const moveItem = async (item, direction) => {
    if (!data) return;
    const list = data.items;
    const idx = list.findIndex((l) => l.id === item.id);
    const target = direction === "up" ? idx - 1 : idx + 1;
    if (target < 0 || target >= list.length) return;
    const a = list[idx];
    const b = list[target];
    try {
      await Promise.all([
        api.patch(`/me/link-page/items/${a.id}`, { order: b.order || 0 }),
        api.patch(`/me/link-page/items/${b.id}`, { order: a.order || 0 }),
      ]);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke flytte");
    }
  };

  const uploadQr = async (item, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/me/link-page/items/${item.id}/qr`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("QR-kode lastet opp");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke laste opp QR");
    }
  };

  const deleteQr = async (item) => {
    try {
      await api.delete(`/me/link-page/items/${item.id}/qr`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke fjerne QR");
    }
  };

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Kunne ikke kopiere");
    }
  };

  const regenerateSlug = async () => {
    if (!window.confirm("Vil du bytte til ny lenke? Den gamle URL-en slutter å fungere umiddelbart.")) return;
    try {
      const r = await api.post("/me/link-page/regenerate-slug");
      toast.success(`Ny lenke: ${r.data.slug}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke bytte");
    }
  };

  if (!data) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto py-10 px-4">
          <div className="font-manrope text-sm text-[var(--cream-soft)]">Laster…</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-3xl mx-auto py-8 px-4 space-y-8">
        <RouterLink
          to="/skaper/studio"
          className="inline-flex items-center gap-2 text-sm font-manrope text-[var(--cream-soft)] hover:text-[var(--cream)]"
          data-testid="back-to-studio"
        >
          <ArrowLeft className="h-4 w-4" /> Tilbake til Skaper-studio
        </RouterLink>

        <div>
          <h1 className="font-fraunces italic text-4xl text-[var(--cream)] mb-2">Min link-side</h1>
          <p className="font-manrope text-sm text-[var(--cream-soft)] leading-relaxed max-w-xl">
            En offentlig samleside med dine egne lenker. Trygg å dele på Snap, Instagram og andre steder
            — første siden viser bare navnet ditt og en «Se mine linker»-knapp. Alderskontroll skjer bak
            klikket. Du bestemmer selv hvilke lenker som vises, ingen godkjenning kreves.
          </p>
        </div>

        {/* Public URL */}
        <div className="border border-white/10 p-5 space-y-3">
          <div className="font-manrope text-xs text-[var(--cream-soft)] uppercase tracking-wide">Offentlig lenke</div>
          <div className="flex items-center gap-3">
            <code className="flex-1 font-mono text-sm text-[var(--cream)] break-all" data-testid="link-page-url">
              {publicUrl}
            </code>
            <button
              onClick={copyUrl}
              className="btn-ghost text-xs px-3 py-2 shrink-0 inline-flex items-center gap-2"
              data-testid="copy-link-btn"
            >
              {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
              {copied ? "Kopiert" : "Kopier"}
            </button>
            <a
              href={publicUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-ghost text-xs px-3 py-2 shrink-0 inline-flex items-center gap-2"
              data-testid="open-link-btn"
            >
              <ExternalLink className="h-3 w-3" /> Åpne
            </a>
          </div>
          <div className="flex items-center justify-between gap-3 pt-2">
            <div className="font-manrope text-xs text-[var(--cream-soft)]">
              Tilfeldig kode i URL-en beskytter mot triggerord i brukernavn.
            </div>
            <button
              onClick={regenerateSlug}
              className="btn-ghost text-xs px-3 py-1.5 shrink-0"
              data-testid="regenerate-slug-btn"
              title="Bytt til ny tilfeldig lenke"
            >
              Bytt lenke
            </button>
          </div>
          {!enabled && (
            <div className="border-l-2 border-[var(--magenta)] pl-3 py-1 text-xs text-[var(--cream-soft)]">
              Link-siden er skrudd av. Aktiver den under for at lenken skal fungere for besøkende.
            </div>
          )}
        </div>

        {/* Settings */}
        <div className="border border-white/10 p-5 space-y-5">
          <h2 className="font-fraunces italic text-xl text-[var(--cream)]">Innstillinger</h2>

          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="font-manrope text-sm text-[var(--cream)] font-medium">Aktiver link-side</div>
              <div className="font-manrope text-xs text-[var(--cream-soft)]">
                Når skrudd av vises 404 på den offentlige URL-en.
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-testid="toggle-enabled">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-white/10 peer-checked:bg-[var(--magenta)] rounded-full peer transition-colors relative after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:after:translate-x-5" />
            </label>
          </div>

          <div className="border-l-2 border-yellow-500/50 pl-3 py-2 flex items-start gap-2 text-xs text-[var(--cream-soft)]">
            <AlertTriangle className="h-3.5 w-3.5 text-yellow-500 shrink-0 mt-0.5" />
            <div>
              <div className="text-[var(--cream)] font-medium mb-1">Bio er det ENESTE Snap ser</div>
              Første siden av link-siden viser bare avatar, navn og bio — dette er alt Snap-boten får tak i.
              Bruk aldri ordene «18+», «adult», «erotisk», «xxx», «voksen», «porno», «OnlyFans»,
              «Fansly» eller lignende her, det kan blokke lenken din. Hold bio nøytral,
              f.eks: «Skaper fra Norge. Finn meg her ↓».
              <div className="mt-2 text-[var(--cream-soft)]">
                Titler og URL-er på lenkene under er skjult bak 18+-cookien — de er trygge uansett innhold.
              </div>
            </div>
          </div>

          <div>
            <label className="block font-manrope text-sm text-[var(--cream)] font-medium mb-2">
              Kort bio (valgfritt) <span className="text-[var(--cream-soft)]">— maks 200 tegn</span>
            </label>
            <textarea
              value={bio}
              onChange={(e) => setBio(e.target.value.slice(0, 200))}
              rows={3}
              placeholder="F.eks. «Skaper fra Norge. Finn meg her ↓»"
              className="w-full bg-transparent border border-white/20 focus:border-[var(--magenta)] outline-none px-3 py-2 text-sm text-[var(--cream)] font-manrope"
              data-testid="bio-input"
            />
            <div className="text-right text-xs text-[var(--cream-soft)] font-manrope mt-1">
              {bio.length}/200
            </div>
            <SensitiveWarning text={bio} className="mt-2" />
          </div>

          <div className="flex justify-end">
            <button
              onClick={saveSettings}
              disabled={saving}
              className="btn-magenta text-sm px-6"
              data-testid="save-settings-btn"
            >
              {saving ? "Lagrer…" : "Lagre innstillinger"}
            </button>
          </div>
        </div>

        {/* Legg til ny lenke */}
        <div className="border border-white/10 p-5 space-y-4">
          <h2 className="font-fraunces italic text-xl text-[var(--cream)]">Legg til lenke</h2>
          <p className="font-manrope text-xs text-[var(--cream-soft)]">
            Legg til så mange lenker du vil (maks 30). De vises umiddelbart, ingen godkjenning kreves.
            Titler og URL-er er skjult for Snap og andre bots (bak 18+-cookien), så du kan trygt bruke
            hva som helst her.
          </p>

          <div className="grid gap-3">
            <div>
              <label className="block font-manrope text-xs text-[var(--cream-soft)] mb-1">Tittel</label>
              <input
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value.slice(0, 60))}
                placeholder="F.eks. «Nettbutikk» eller «Bestill her»"
                className="w-full bg-transparent border border-white/20 focus:border-[var(--magenta)] outline-none px-3 py-2 text-sm text-[var(--cream)]"
                data-testid="new-label-input"
              />
            </div>
            <div>
              <label className="block font-manrope text-xs text-[var(--cream-soft)] mb-1">URL</label>
              <input
                value={newUrl}
                onChange={(e) => setNewUrl(e.target.value.slice(0, 500))}
                placeholder="https://..."
                className="w-full bg-transparent border border-white/20 focus:border-[var(--magenta)] outline-none px-3 py-2 text-sm text-[var(--cream)] font-mono"
                data-testid="new-url-input"
              />
            </div>
            <div className="flex justify-end">
              <button
                onClick={addItem}
                disabled={adding || !newLabel.trim() || !newUrl.trim()}
                className="btn-magenta text-sm px-6 inline-flex items-center gap-2 disabled:opacity-50"
                data-testid="add-item-btn"
              >
                <Plus className="h-4 w-4" /> {adding ? "Legger til…" : "Legg til"}
              </button>
            </div>
          </div>
        </div>

        {/* Liste */}
        <div className="border border-white/10 p-5 space-y-4">
          <div>
            <h2 className="font-fraunces italic text-xl text-[var(--cream)]">Mine lenker</h2>
            <p className="font-manrope text-xs text-[var(--cream-soft)] mt-1">
              Toggle på/av per lenke. Bruk pilene for å endre rekkefølge. Trykk QR-ikonet for å laste opp
              en QR-kode som vises ved siden av lenken.
            </p>
          </div>

          {data.items.length === 0 && (
            <div className="border-l-2 border-white/20 pl-3 py-2 text-sm text-[var(--cream-soft)] font-manrope">
              Ingen lenker enda. Legg til din første over.
            </div>
          )}

          <div className="space-y-2" data-testid="link-list">
            {data.items.map((item, i) => (
              <LinkItemRow
                key={item.id}
                item={item}
                isFirst={i === 0}
                isLast={i === data.items.length - 1}
                onEdit={setEditing}
                onDelete={deleteItem}
                onMove={moveItem}
                onUploadQr={uploadQr}
                onDeleteQr={deleteQr}
                onToggle={toggleItem}
              />
            ))}
          </div>
        </div>
      </div>

      {editing && (
        <EditModal
          item={editing}
          onClose={() => setEditing(null)}
          onSave={(updates) => editItem(editing, updates)}
        />
      )}
    </Layout>
  );
}
